import React, { useState, useEffect, useRef } from 'react';
import { initFeatherIcons } from './utils/themeConfig';
import { useNavigate, useLocation } from 'react-router-dom';
import { decodePinJwt, getTokensFromStorage, clearTokens } from './utils/token';
import { storeCurrentStep, getStoredStep, storeVisitStatus, getStoredVisitStatus } from './utils/sessionStorage';
import YearOfBirthVerification from './components/YearOfBirthVerification';
import DeviceCheck from './components/DeviceCheck';
import VoiceIntakeForms from './components/VoiceIntakeForms'; // Import the new voice component
import InsuranceVerification from './components/InsuranceVerification';
import CheckInComplete from './components/checkinComplete/CheckinComplete';

const CheckInFlowWithVoice = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get step from URL query parameter
  const getStepFromUrl = () => {
    const params = new URLSearchParams(location.search);
    const step = parseInt(params.get('step'), 10);
    return isNaN(step) ? 0 : Math.min(Math.max(0, step), 4); // Ensure step is between 0 and 4
  };
  
  const [currentStep, setCurrentStep] = useState(getStepFromUrl());
  const [tokenData, setTokenData] = useState(null);
  const [tokenError, setTokenError] = useState('');
  const [checkInData, setCheckInData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isReturningUser, setIsReturningUser] = useState(false);
  const [storedStepNumber, setStoredStepNumber] = useState(null);
  
  
  // Steps in the check-in process
  const steps = [
    { name: 'Verify Identity', component: YearOfBirthVerification },
    { name: 'Check Devices', component: DeviceCheck },
    { name: 'Voice Check-In', component: VoiceIntakeForms }, // Use the new voice component
    { name: 'Verify Insurance', component: InsuranceVerification },
    { name: 'Ready for Visit', component: CheckInComplete }
  ];
  
  // Track the highest step reached
  const [maxStepReached, setMaxStepReached] = useState(0);
  
  useEffect(() => {
    // Check for stored tokens
    const storedTokens = getTokensFromStorage();

    if (storedTokens && storedTokens.pin_jwt) {
      // If tokens are found, decode the PIN-JWT
      decodePinJwt(storedTokens.pin_jwt).then(tokenData => {
        if (tokenData && !tokenData.error) {
          setTokenData(tokenData);

          // Check if there's a stored step for this patient based on token data
          if (tokenData.patient_info?.id) {
            const storedStep = getStoredStep(tokenData.patient_info.id);
            if (storedStep !== null && storedStep > 0) {
              console.log(`Found stored step: ${storedStep} for patient: ${tokenData.patient_info.id}`);
              setStoredStepNumber(storedStep);
              setIsReturningUser(true);

              // Only force navigation to step 0 on initial load, not during navigation
              if (getStepFromUrl() === 0) {
                setMaxStepReached(Math.max(storedStep, maxStepReached));
              }
            } else {
              // Initialize visit status for new users (or users with expired tokens)
              storeVisitStatus('inProgress', tokenData.patient_info.id);
            }
          }
          setIsLoading(false);
        } else {
          console.error('Failed to decode stored token:', tokenData?.message);
          setTokenError('Invalid or expired token. Please restart the check-in process.');
          setIsLoading(false);
          // Optionally clear invalid tokens
          clearTokens(); // Assuming clearTokens is imported
        }
      });
    } else {
      // No stored tokens found, proceed to YOB verification
      setIsLoading(false);
      // The YearOfBirthVerification component will handle getting the initial tokens
    }
  }, []); // Empty dependency array means this effect runs once on mount
  
  
  
  // Update maxStepReached when currentStep increases
  useEffect(() => {
    if (currentStep > maxStepReached) {
      setMaxStepReached(currentStep);
    }
  }, [currentStep, maxStepReached]);
  
  // Update URL when step changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    params.set('step', currentStep.toString());
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  }, [currentStep, location.pathname, location.search, navigate]);
  
  // Store current step in localStorage when it changes
  useEffect(() => {
    if (tokenData && tokenData.patient_id && currentStep > 0) {
      storeCurrentStep(currentStep, tokenData.patient_id);
      
      // Also update visit status
      storeVisitStatus('inProgress', tokenData.patient_id);
    }
  }, [currentStep, tokenData]);
  
  // Function to navigate to a specific step
  const goToStep = (stepIndex) => {
    // Only allow going to steps that have been completed or the current step
    if (stepIndex <= maxStepReached) {
      setCurrentStep(stepIndex);
    }
  };
  
  // Handle step completion
  const handleStepComplete = (stepData = {}) => {
    // Save data from the completed step
    setCheckInData(prevData => ({
      ...prevData,
      ...stepData
    }));
    
    // For returning users who just verified their DOB (step 0),
    // redirect them to their previously stored step
    if (isReturningUser && currentStep === 0 && storedStepNumber !== null) {
      console.log(`Redirecting returning user to previously stored step: ${storedStepNumber}`);
      setCurrentStep(storedStepNumber);
      setIsReturningUser(false); // Reset the flag after redirection
      return;
    }
    
    // For normal flow, move to the next step
    const nextStep = currentStep + 1;
    setCurrentStep(nextStep);
    
    // Update max step reached if needed
    if (nextStep > maxStepReached) {
      setMaxStepReached(nextStep);
    }
    
    // If this is the final step, update visit status to complete
    if (nextStep >= steps.length) {
      if (tokenData && tokenData.patient_id) {
        storeVisitStatus('complete', tokenData.patient_id);
      }
    }
  };
  
  // Render the current step component
  const renderCurrentStep = () => {
    if (isLoading) {
      return (
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading check-in process...</p>
        </div>
      );
    }
    
    if (tokenError) {
      return (
        <div className="card">
          <div className="card-body">
            <div className="alert alert-danger">
              <h4 className="alert-heading">Oops!</h4>
              <p>{tokenError}</p>
            </div>
            <p>
              If you continue to experience issues, please contact your healthcare provider for assistance.
            </p>
          </div>
        </div>
      );
    }
    
    const CurrentStepComponent = steps[currentStep].component;
    
    return (
      <CurrentStepComponent
        tokenData={tokenData}
        onSuccess={handleStepComplete}
        checkInData={checkInData}
      />
    );
  };
  return (
    <div className="container mt-4">
      <div className="header text-center">
        <h2>Voice Check-In Flow</h2>
        <p className="small">Fast, easy and smart checkin experience!</p>
        <p>Let&apos;s get you ready for a visit with your provider in 5 minutes or less</p>
      </div>
      
      {!isLoading && !tokenError && (
        <div className="step-indicator mb-4">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`step ${index === currentStep ? 'active' : ''} ${
                index < currentStep ? 'completed' : ''
              } ${index <= maxStepReached ? 'clickable' : ''}`}
              onClick={() => index <= maxStepReached && goToStep(index)}
              style={{ cursor: index <= maxStepReached ? 'pointer' : 'default' }}
              title={index <= maxStepReached ? `Go to ${step.name}` : 'Not available yet'}
            >
              <div className="step-number">
                {index < currentStep ? (
                  <i className="fas fa-check feather-sm"></i>
                ) : (
                  index + 1
                )}
              </div>
              <div className="step-title">{step.name}</div>
            </div>
          ))}
        </div>
      )}
      
      {renderCurrentStep()}
    </div>
  );
};

export default CheckInFlowWithVoice;